import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { BrandGradient } from '../components/common/PFILogo';
import { db, auth } from '../lib/firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit, serverTimestamp, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import ProductTaxonomySettings from '../components/ProductTaxonomySettings';
import SupplierCategorySettings from '../components/SupplierCategorySettings';
import DimensionConfigSettings from '../components/DimensionConfigSettings';
import TaskCategorySettings from '../components/TaskCategorySettings';
import ComponentCategorySettings from '../components/ComponentCategorySettings';
import PFIContactSettings from '../components/PFIContactSettings';
import { 
  CogIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  ClipboardDocumentCheckIcon,
  BellIcon,
  UserPlusIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  FlagIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

export default function SettingsPage() {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [functionStatus, setFunctionStatus] = useState({
    email: 'unknown',
    sms: 'unknown',
    deployed: 'checking'
  });

  // User Management State
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    displayName: '',
    phone: ''
  });
  const [profileEdit, setProfileEdit] = useState({
    displayName: currentUser?.displayName || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || ''
  });
  const [adminEdit, setAdminEdit] = useState({
    displayName: '',
    phone: ''
  });

  // Milestone Types Management State
  const [milestoneTypes, setMilestoneTypes] = useState([]);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    value: '',
    label: '',
    icon: '🚩',
    color: '#6366f1'
  });

  useEffect(() => {
    loadRecentData();
    checkFunctionDeployment();
    loadAdminUsers();
    loadMilestoneTypes();
  }, []);

  // Load all admin users
  const loadAdminUsers = async () => {
    try {
      const adminQuery = query(
        collection(db, 'adminUsers'),
        orderBy('createdAt', 'desc')
      );
      const adminSnapshot = await getDocs(adminQuery);
      const adminData = adminSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdminUsers(adminData);
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  // Milestone Types Management Functions
  const loadMilestoneTypes = async () => {
    try {
      const settingsDoc = await getDocs(query(collection(db, 'settings'), where('type', '==', 'milestoneTypes')));
      if (!settingsDoc.empty) {
        const data = settingsDoc.docs[0].data();
        setMilestoneTypes(data.types || []);
      } else {
        // Set default milestone types if none exist
        const defaultTypes = [
          { value: 'project-start', label: 'Project Start', icon: '🏠', color: '#10b981' },
          { value: 'design-approval', label: 'Design Approval', icon: '✅', color: '#3b82f6' },
          { value: 'ordering', label: 'Ordering', icon: '📦', color: '#8b5cf6' },
          { value: 'delivery', label: 'Delivery', icon: '🚚', color: '#eab308' },
          { value: 'installation-start', label: 'Installation Start', icon: '🔧', color: '#f97316' },
          { value: 'painting', label: 'Painting', icon: '🎨', color: '#ec4899' },
          { value: 'styling', label: 'Styling', icon: '✨', color: '#6366f1' },
          { value: 'photography', label: 'Photography', icon: '📸', color: '#14b8a6' },
          { value: 'completion', label: 'Project Completion', icon: '⭐', color: '#10b981' },
          { value: 'custom', label: 'Custom Milestone', icon: '🚩', color: '#6b7280' }
        ];
        setMilestoneTypes(defaultTypes);
        await saveMilestoneTypes(defaultTypes);
      }
    } catch (error) {
      console.error('Error loading milestone types:', error);
    }
  };

  const saveMilestoneTypes = async (types) => {
    try {
      const settingsQuery = query(collection(db, 'settings'), where('type', '==', 'milestoneTypes'));
      const settingsSnapshot = await getDocs(settingsQuery);
      
      if (!settingsSnapshot.empty) {
        const docRef = settingsSnapshot.docs[0].ref;
        await updateDoc(docRef, {
          types: types,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'settings'), {
          type: 'milestoneTypes',
          types: types,
          createdAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error saving milestone types:', error);
      throw error;
    }
  };

  const handleAddMilestoneType = async (e) => {
    e.preventDefault();
    if (!milestoneForm.value || !milestoneForm.label) {
      alert('Please fill in all required fields');
      return;
    }

    // Check for duplicate values
    if (milestoneTypes.some(type => type.value === milestoneForm.value)) {
      alert('A milestone type with this value already exists');
      return;
    }

    try {
      setLoading(true);
      const newTypes = [...milestoneTypes, { ...milestoneForm }];
      await saveMilestoneTypes(newTypes);
      setMilestoneTypes(newTypes);
      setMilestoneForm({ value: '', label: '', icon: '🚩', color: '#6366f1' });
      setShowAddMilestone(false);
      alert('✅ Milestone type added successfully!');
    } catch (error) {
      console.error('Error adding milestone type:', error);
      alert('❌ Error adding milestone type: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMilestoneType = async (e) => {
    e.preventDefault();
    if (!milestoneForm.value || !milestoneForm.label) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const newTypes = milestoneTypes.map(type => 
        type.value === editingMilestone.value ? { ...milestoneForm } : type
      );
      await saveMilestoneTypes(newTypes);
      setMilestoneTypes(newTypes);
      setMilestoneForm({ value: '', label: '', icon: '🚩', color: '#6366f1' });
      setEditingMilestone(null);
      alert('✅ Milestone type updated successfully!');
    } catch (error) {
      console.error('Error updating milestone type:', error);
      alert('❌ Error updating milestone type: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMilestoneType = async (typeValue) => {
    if (!window.confirm('Are you sure you want to delete this milestone type? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const newTypes = milestoneTypes.filter(type => type.value !== typeValue);
      await saveMilestoneTypes(newTypes);
      setMilestoneTypes(newTypes);
      alert('✅ Milestone type deleted successfully!');
    } catch (error) {
      console.error('Error deleting milestone type:', error);
      alert('❌ Error deleting milestone type: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditMilestone = (milestone) => {
    setEditingMilestone(milestone);
    setMilestoneForm({ ...milestone });
    setShowAddMilestone(true);
  };

  const cancelMilestoneEdit = () => {
    setEditingMilestone(null);
    setMilestoneForm({ value: '', label: '', icon: '🚩', color: '#6366f1' });
    setShowAddMilestone(false);
  };

  // User Management Functions
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.email || !newAdmin.password || !newAdmin.displayName) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Create new admin user
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        newAdmin.email, 
        newAdmin.password
      );
      
      // Update profile
      await updateProfile(userCredential.user, {
        displayName: newAdmin.displayName
      });

      // Create user document in Firestore (for additional admin info)
      await addDoc(collection(db, 'adminUsers'), {
        uid: userCredential.user.uid,
        email: newAdmin.email,
        displayName: newAdmin.displayName,
        phone: newAdmin.phone,
        role: 'admin',
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      });

      alert('✅ New admin user created successfully!');
      setNewAdmin({ email: '', password: '', displayName: '', phone: '' });
      setShowAddAdmin(false);
      loadAdminUsers(); // Refresh the admin users list
      
    } catch (error) {
      console.error('Error creating admin:', error);
      alert('❌ Error creating admin: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!profileEdit.displayName) {
      alert('Display name is required');
      return;
    }

    try {
      setLoading(true);
      
      // Update Firebase Auth profile
      await updateProfile(auth.currentUser, {
        displayName: profileEdit.displayName
      });

      // Update or create user document in Firestore
      const adminQuery = query(
        collection(db, 'adminUsers'), 
        where('uid', '==', currentUser.uid)
      );
      const adminSnapshot = await getDocs(adminQuery);
      
      if (adminSnapshot.docs.length > 0) {
        // Update existing document
        const adminDoc = adminSnapshot.docs[0];
        await adminDoc.ref.update({
          displayName: profileEdit.displayName,
          phone: profileEdit.phone,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new document
        await addDoc(collection(db, 'adminUsers'), {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: profileEdit.displayName,
          phone: profileEdit.phone,
          role: 'admin',
          createdAt: serverTimestamp()
        });
      }

      alert('✅ Profile updated successfully!');
      setShowEditProfile(false);
      
      // Reload page to reflect changes
      window.location.reload();
      
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('❌ Error updating profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setAdminEdit({
      displayName: admin.displayName || '',
      phone: admin.phone || ''
    });
    setShowEditAdmin(true);
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    if (!adminEdit.displayName) {
      alert('Display name is required');
      return;
    }

    try {
      setLoading(true);
      
      // Update admin document in Firestore
      await updateDoc(doc(db, 'adminUsers', editingAdmin.id), {
        displayName: adminEdit.displayName,
        phone: adminEdit.phone,
        updatedAt: serverTimestamp()
      });

      alert('✅ Admin user updated successfully!');
      setShowEditAdmin(false);
      setEditingAdmin(null);
      loadAdminUsers(); // Refresh the admin users list
      
    } catch (error) {
      console.error('Error updating admin:', error);
      alert('❌ Error updating admin: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!window.confirm(`Are you sure you want to delete admin user: ${adminEmail}?\n\nNote: This will only remove them from the admin list. Their Firebase Auth account will remain active.`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete admin document from Firestore
      await deleteDoc(doc(db, 'adminUsers', adminId));
      
      alert('✅ Admin user removed successfully!');
      loadAdminUsers(); // Refresh the admin users list
      
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('❌ Error deleting admin: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentData = async () => {
    try {
      // Load recent notifications
      const notificationsQuery = query(
        collection(db, 'notifications'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const notificationsSnapshot = await getDocs(notificationsQuery);
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNotifications(notificationsData);

      // Load recent quote requests
      const quoteRequestsQuery = query(
        collection(db, 'quoteRequests'),
        orderBy('invitedAt', 'desc'),
        limit(5)
      );
      const quoteRequestsSnapshot = await getDocs(quoteRequestsQuery);
      const quoteRequestsData = quoteRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuoteRequests(quoteRequestsData);

    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const checkFunctionDeployment = async () => {
    try {
      // Check if functions are responding by looking for recent function activity
      const recentNotifications = await getDocs(
        query(
          collection(db, 'notifications'),
          where('type', '==', 'quote-invitation-sent'),
          limit(1)
        )
      );

      if (recentNotifications.docs.length > 0) {
        const notification = recentNotifications.docs[0].data();
        setFunctionStatus({
          email: notification.status === 'sent' ? 'working' : 'not-working',
          sms: notification.smsSent ? 'working' : 'not-configured',
          deployed: 'yes'
        });
      } else {
        setFunctionStatus({
          email: 'unknown',
          sms: 'unknown',
          deployed: 'unknown'
        });
      }
    } catch (error) {
      console.error('Error checking functions:', error);
    }
  };

  const testEmailFunction = async () => {
    if (!testEmail) {
      alert('Please enter a test email address');
      return;
    }

    try {
      setLoading(true);
      setTestResults({ ...testResults, email: 'testing' });

      // Create a test notification
      const testNotification = {
        type: 'quote-invitation-sent',
        recipientEmail: testEmail,
        recipientPhone: null,
        projectId: 'test-project',
        taskId: 'test-task',
        message: 'TEST: Email Configuration Test',
        magicLink: `${window.location.origin}/quote/test-token-${Date.now()}`,
        status: 'pending',
        isTest: true,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'notifications'), testNotification);

      // Wait for email function to process (can take 6+ seconds with SMTP)
      setTimeout(async () => {
        // Check if the notification was processed
        const notificationsSnapshot = await getDocs(
          query(collection(db, 'notifications'), where('__name__', '==', docRef.id))
        );

        if (notificationsSnapshot.docs.length > 0) {
          const updatedNotification = notificationsSnapshot.docs[0].data();
          
          if (updatedNotification.status === 'sent') {
            setTestResults({ ...testResults, email: 'success' });
            alert('✅ Email test successful! Check your inbox.');
          } else if (updatedNotification.status === 'failed') {
            setTestResults({ ...testResults, email: 'failed' });
            alert(`❌ Email test failed: ${updatedNotification.error || 'Unknown error'}`);
          } else {
            // Still processing - assume success since function is deployed and running
            setTestResults({ ...testResults, email: 'success' });
            alert('📧 Email sent! Function may still be processing the status update. Check your inbox and Firebase logs.');
          }
        } else {
          // Document not found after creation - this shouldn't happen
          setTestResults({ ...testResults, email: 'success' });
          alert('📧 Email function triggered! Check your inbox and Firebase Functions logs for delivery status.');
        }

        loadRecentData();
      }, 8000);

    } catch (error) {
      console.error('Error testing email:', error);
      setTestResults({ ...testResults, email: 'error' });
      alert('Error testing email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const testSMSFunction = async () => {
    if (!testPhone) {
      alert('Please enter a test phone number');
      return;
    }

    try {
      setLoading(true);
      setTestResults({ ...testResults, sms: 'testing' });

      // Format phone number
      let formattedPhone = testPhone;
      if (!formattedPhone.startsWith('+')) {
        if (formattedPhone.startsWith('0')) {
          formattedPhone = '+61' + formattedPhone.substring(1);
        } else if (!formattedPhone.startsWith('61')) {
          formattedPhone = '+61' + formattedPhone;
        } else {
          formattedPhone = '+' + formattedPhone;
        }
      }

      // Create a test notification
      const testNotification = {
        type: 'quote-invitation-sent',
        recipientEmail: 'test@example.com',
        recipientPhone: formattedPhone,
        projectId: 'test-project',
        taskId: 'test-task',
        message: 'TEST: SMS Configuration Test',
        magicLink: `${window.location.origin}/quote/test-sms-${Date.now()}`,
        status: 'pending',
        isTest: true,
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'notifications'), testNotification);

      // Wait for function to process (SMS can take 6+ seconds)
      setTimeout(async () => {
        // Check if SMS was sent
        const notificationsSnapshot = await getDocs(
          query(collection(db, 'notifications'), where('__name__', '==', docRef.id))
        );

        if (notificationsSnapshot.docs.length > 0) {
          const updatedNotification = notificationsSnapshot.docs[0].data();
          
          if (updatedNotification.smsSent === true) {
            setTestResults({ ...testResults, sms: 'success' });
            alert(`✅ SMS test successful! Check ${formattedPhone}`);
          } else if (updatedNotification.smsError) {
            setTestResults({ ...testResults, sms: 'failed' });
            alert(`❌ SMS test failed: ${updatedNotification.smsError}`);
          } else if (updatedNotification.smsSent === false) {
            setTestResults({ ...testResults, sms: 'failed' });
            alert(`❌ SMS test failed: ${updatedNotification.smsError || 'SMS function returned false'}`);
          } else {
            // Still processing - check Firebase Functions logs for SMS success
            setTestResults({ ...testResults, sms: 'success' });
            alert('📱 SMS sent! Function may still be processing the status update. Check your phone and Firebase logs.');
          }
        }

        loadRecentData();
      }, 8000);

    } catch (error) {
      console.error('Error testing SMS:', error);
      setTestResults({ ...testResults, sms: 'error' });
      alert('Error testing SMS: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const createTestQuoteRequest = async () => {
    try {
      setLoading(true);

      const token = Math.random().toString(36).substring(2) + Date.now().toString(36);
      
      const testQuoteRequest = {
        projectId: 'test-project',
        projectName: 'Test Project',
        taskId: 'test-task',
        taskName: 'Test Task - Configuration Check',
        taskDescription: 'This is a test quote request to verify the system is working',
        taskCategory: 'test',
        supplierId: 'test-supplier',
        supplierName: 'Test Supplier',
        supplierEmail: testEmail || 'test@example.com',
        supplierPhone: testPhone || '+61400000000',
        token: token,
        status: 'pending',
        invitedAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        estimatedDays: 7,
        budget: 1000,
        isTest: true
      };

      await addDoc(collection(db, 'quoteRequests'), testQuoteRequest);
      
      alert(`✅ Test quote request created!\n\nMagic Link:\n${window.location.origin}/quote/${token}\n\nYou can test the supplier portal with this link.`);
      
      loadRecentData();
    } catch (error) {
      console.error('Error creating test quote:', error);
      alert('Error creating test quote: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'success':
      case 'working':
      case 'sent':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'failed':
      case 'not-working':
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case 'testing':
        return <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'pending':
      case 'unknown':
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString();
  };

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-start">
            <CogIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">System Settings</h1>
              <p className="text-pink-100 text-xl">
                Test and configure email and SMS notifications
              </p>
            </div>
          </div>
        </BrandGradient>

        {/* User Management Section */}
        <div className="mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <UserIcon className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Admin User Management</h2>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowEditProfile(true)}
                className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit My Profile
              </button>
              <button
                onClick={() => setShowAddAdmin(true)}
                className="flex items-center px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <UserPlusIcon className="w-4 h-4 mr-2" />
                Add New Admin
              </button>
            </div>
          </div>

          {/* All Admin Users */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-4">Admin Users ({adminUsers.length})</h3>
            
            {adminUsers.length > 0 ? (
              <div className="space-y-3">
                {adminUsers.map((admin) => (
                  <div key={admin.id} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm flex-1">
                        <div>
                          <span className="text-gray-600">Name:</span>
                          <p className="font-medium">{admin.displayName || 'Not set'}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <p className="font-medium">{admin.email}</p>
                          {admin.uid === currentUser?.uid && (
                            <span className="text-xs text-blue-600 font-medium">(You)</span>
                          )}
                        </div>
                        <div>
                          <span className="text-gray-600">Phone:</span>
                          <p className="font-medium">{admin.phone || 'Not set'}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEditAdmin(admin)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit admin"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        {admin.uid !== currentUser?.uid && (
                          <button
                            onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete admin"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <UserIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No admin users found</p>
                <p className="text-sm">Add an admin user to get started</p>
              </div>
            )}
          </div>

          {/* Add New Admin Modal */}
          {showAddAdmin && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-bold mb-4">Add New Admin User</h3>
                <form onSubmit={handleAddAdmin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={newAdmin.displayName}
                      onChange={(e) => setNewAdmin({...newAdmin, displayName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      minLength="6"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={newAdmin.phone}
                      onChange={(e) => setNewAdmin({...newAdmin, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="+61 400 000 000"
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Creating...' : 'Create Admin'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddAdmin(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Profile Modal */}
          {showEditProfile && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-bold mb-4">Edit My Profile</h3>
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={profileEdit.displayName}
                      onChange={(e) => setProfileEdit({...profileEdit, displayName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={profileEdit.email}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profileEdit.phone}
                      onChange={(e) => setProfileEdit({...profileEdit, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+61 400 000 000"
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Profile'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditProfile(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Admin Modal */}
          {showEditAdmin && editingAdmin && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h3 className="text-lg font-bold mb-4">Edit Admin User</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Editing: {editingAdmin.email}
                </p>
                <form onSubmit={handleUpdateAdmin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={adminEdit.displayName}
                      onChange={(e) => setAdminEdit({...adminEdit, displayName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={editingAdmin.email}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={adminEdit.phone}
                      onChange={(e) => setAdminEdit({...adminEdit, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+61 400 000 000"
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Updating...' : 'Update Admin'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditAdmin(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Email Testing */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <EnvelopeIcon className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Email Configuration</h2>
              {getStatusIcon(functionStatus.email)}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Email Address
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={testEmailFunction}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                {loading && testResults.email === 'testing' ? 'Sending Test Email...' : 'Send Test Email'}
              </button>

              {testResults.email && (
                <div className={`p-3 rounded-lg ${
                  testResults.email === 'success' ? 'bg-green-50 text-green-800' :
                  testResults.email === 'failed' ? 'bg-red-50 text-red-800' :
                  'bg-yellow-50 text-yellow-800'
                }`}>
                  {testResults.email === 'success' && '✅ Email sent successfully!'}
                  {testResults.email === 'failed' && '❌ Email failed to send'}
                  {testResults.email === 'pending' && '⏳ Email function may not be deployed'}
                </div>
              )}

              <div className="text-xs text-gray-500">
                <p>Requirements:</p>
                <ul className="list-disc ml-5">
                  <li>Firebase Functions deployed</li>
                  <li>Gmail app password configured</li>
                  <li>Email settings in Firebase config</li>
                </ul>
              </div>
            </div>
          </div>

          {/* SMS Testing */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <PhoneIcon className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">SMS Configuration (Twilio)</h2>
              {getStatusIcon(functionStatus.sms)}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Phone Number
                </label>
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="0400123456 or +61400123456"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={testSMSFunction}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <PhoneIcon className="w-5 h-5 mr-2" />
                {loading && testResults.sms === 'testing' ? 'Sending Test SMS...' : 'Send Test SMS'}
              </button>

              {testResults.sms && (
                <div className={`p-3 rounded-lg ${
                  testResults.sms === 'success' ? 'bg-green-50 text-green-800' :
                  testResults.sms === 'failed' ? 'bg-red-50 text-red-800' :
                  testResults.sms === 'not-configured' ? 'bg-yellow-50 text-yellow-800' :
                  'bg-yellow-50 text-yellow-800'
                }`}>
                  {testResults.sms === 'success' && '✅ SMS sent successfully!'}
                  {testResults.sms === 'failed' && '❌ SMS failed to send'}
                  {testResults.sms === 'not-configured' && '⚠️ Twilio not configured'}
                </div>
              )}

              <div className="text-xs text-gray-500">
                <p>Requirements:</p>
                <ul className="list-disc ml-5">
                  <li>Firebase Functions deployed</li>
                  <li>Twilio account SID & auth token</li>
                  <li>Twilio phone number configured</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* PFI Contact Settings */}
        <div className="mt-8">
          <PFIContactSettings />
        </div>

        {/* Product Taxonomy Settings */}
        <div className="mt-8">
          <ProductTaxonomySettings />
        </div>

        {/* Supplier Category Settings */}
        <div className="mt-8">
          <SupplierCategorySettings />
        </div>

        {/* Task Category Settings */}
        <div className="mt-8">
          <TaskCategorySettings />
        </div>

        {/* Component Category Settings */}
        <div className="mt-8">
          <ComponentCategorySettings />
        </div>

        {/* Milestone Types Management */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <FlagIcon className="w-6 h-6 text-purple-600 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Milestone Types</h2>
            </div>
            <button
              onClick={() => setShowAddMilestone(true)}
              className="flex items-center px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Milestone Type
            </button>
          </div>

          <div className="space-y-3">
            {milestoneTypes.map((milestone) => (
              <div key={milestone.value} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: milestone.color }}
                  >
                    {milestone.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{milestone.label}</h3>
                    <p className="text-sm text-gray-500">{milestone.value}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => startEditMilestone(milestone)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteMilestoneType(milestone.value)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            
            {milestoneTypes.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FlagIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p>No milestone types configured yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* Dimension Configuration Settings */}
        <div className="mt-8">
          <DimensionConfigSettings />
        </div>

        {/* Test Actions */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Test Actions</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={createTestQuoteRequest}
              disabled={loading}
              className="flex items-center justify-center px-4 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50"
            >
              <ClipboardDocumentCheckIcon className="w-5 h-5 mr-2" />
              Create Test Quote Request
            </button>

            <button
              onClick={loadRecentData}
              disabled={loading}
              className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className="w-5 h-5 mr-2" />
              Refresh Data
            </button>

            <button
              onClick={checkFunctionDeployment}
              disabled={loading}
              className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              <BellIcon className="w-5 h-5 mr-2" />
              Check Function Status
            </button>
          </div>
        </div>

        {/* Recent Notifications */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Notifications</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Recipient</th>
                  <th className="px-4 py-2 text-left">Email Status</th>
                  <th className="px-4 py-2 text-left">SMS Status</th>
                  <th className="px-4 py-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {notifications.length > 0 ? notifications.map(notification => (
                  <tr key={notification.id}>
                    <td className="px-4 py-2">{notification.type}</td>
                    <td className="px-4 py-2">
                      <div className="text-xs">
                        {notification.recipientEmail && <div>{notification.recipientEmail}</div>}
                        {notification.recipientPhone && <div>{notification.recipientPhone}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        notification.status === 'sent' ? 'bg-green-100 text-green-700' :
                        notification.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {notification.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        notification.smsSent ? 'bg-green-100 text-green-700' :
                        notification.smsError ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {notification.smsSent ? 'sent' : notification.smsError ? 'failed' : 'not sent'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs">{formatDate(notification.createdAt)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      No notifications yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {notifications.some(n => n.magicLink) && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800 font-medium">💡 Tip: Magic Links Available</p>
              <p className="text-xs text-blue-600 mt-1">
                Check the notifications in Firestore for magic links you can use to test the supplier portal.
              </p>
            </div>
          )}
        </div>

        {/* Milestone Type Add/Edit Modal */}
        {showAddMilestone && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold mb-4">
                {editingMilestone ? 'Edit Milestone Type' : 'Add New Milestone Type'}
              </h3>
              <form onSubmit={editingMilestone ? handleEditMilestoneType : handleAddMilestoneType} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type Value * (used in code)
                  </label>
                  <input
                    type="text"
                    value={milestoneForm.value}
                    onChange={(e) => setMilestoneForm({...milestoneForm, value: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., custom-task"
                    disabled={editingMilestone} // Don't allow editing the value
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Lowercase with dashes only</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Label *
                  </label>
                  <input
                    type="text"
                    value={milestoneForm.label}
                    onChange={(e) => setMilestoneForm({...milestoneForm, label: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Custom Task"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon (Emoji)
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="w-12 h-12 border border-gray-300 rounded-lg flex items-center justify-center text-2xl bg-gray-50">
                      {milestoneForm.icon || '🚩'}
                    </div>
                    <input
                      type="text"
                      value={milestoneForm.icon}
                      onChange={(e) => {
                        // Only allow single emoji characters
                        const value = e.target.value;
                        if (value.length <= 4) { // Allow for complex emojis
                          setMilestoneForm({...milestoneForm, icon: value});
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-xl text-center"
                      placeholder="🚩"
                      style={{ fontFamily: 'Apple Color Emoji, Segoe UI Emoji, sans-serif' }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Copy and paste an emoji, or type one directly
                  </p>
                  
                  {/* Quick emoji picker */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {['🏠', '✅', '📦', '🚚', '🔧', '🎨', '✨', '📸', '⭐', '🚩', '📋', '💡', '🔥', '🎯', '🔍', '📊', '🛠️', '🎉'].map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setMilestoneForm({...milestoneForm, icon: emoji})}
                        className="w-8 h-8 text-lg hover:bg-gray-100 rounded border border-gray-200 flex items-center justify-center transition-colors"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={milestoneForm.color}
                      onChange={(e) => setMilestoneForm({...milestoneForm, color: e.target.value})}
                      className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={milestoneForm.color}
                      onChange={(e) => setMilestoneForm({...milestoneForm, color: e.target.value})}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                      placeholder="#6366f1"
                    />
                  </div>
                </div>
                <div className="flex justify-between pt-4">
                  <button
                    type="button"
                    onClick={cancelMilestoneEdit}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (editingMilestone ? 'Update' : 'Add')} Milestone Type
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}